import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    Plus,
    Pencil,
    Trash2,
    X,
    Search,
    ArrowLeft,
    Upload,
    Download,
    FileSpreadsheet,
    AlertTriangle,
} from "lucide-react";
import { useScrudManifest, inferType, humanLabel } from "./useScrudManifest";
import adminApi from "@/api/adminApi";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import FieldRenderer from "@/components/admin/FieldRenderer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const PAGE_SIZE = 50;

function buildInitialState(fields) {
    return fields.reduce((acc, field) => {
        acc[field.name] = field.inputType === "checkbox" ? false : "";
        return acc;
    }, {});
}

function normalizeForSubmit(fields, formState) {
    const payload = {};
    for (const field of fields) {
        const value = formState[field.name];
        if (field.inputType === "checkbox") {
            payload[field.name] = Boolean(value);
            continue;
        }
        if (field.inputType === "number") {
            payload[field.name] = value === "" || value == null ? null : Number(value);
            continue;
        }
        if (field.inputType === "json") {
            continue; // displayed only — never written back
        }
        payload[field.name] = value === "" ? null : value;
    }
    return payload;
}

/** Human summary of a cell value for the list table. */
function renderCell(value, inputType) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (inputType === "json" || typeof value === "object") {
        // A whole nested document never fits a grid cell; say what's in it
        // and let the row dialog show the real thing.
        if (Array.isArray(value)) {
            return value.length === 0 ? "—" : `${value.length} item${value.length === 1 ? "" : "s"}`;
        }
        const keys = Object.keys(value);
        return keys.length === 0 ? "—" : `{ ${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ", …" : ""} }`;
    }
    if (inputType === "wysiwyg") {
        // strip HTML for the summary column
        const text = String(value)
            .replace(/<[^>]+>/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        return text.length > 60 ? text.slice(0, 60) + "…" : text;
    }
    const s = String(value);
    return s.length > 60 ? s.slice(0, 60) + "…" : s;
}

export default function AdminScrudPage() {
    const { route } = useParams();
    const navigate = useNavigate();
    const {
        tables,
        error: manifestError,
        loading: manifestLoading,
    } = useScrudManifest();

    // The registry is served by the API, so a column list here always matches
    // what the backend will actually return.
    const config = useMemo(
        () => tables?.find((t) => t.routePath === route),
        [tables, route]
    );

    const columns = useMemo(
        () =>
            (config?.columns ?? []).map((c) => ({
                ...c,
                label: c.label || humanLabel(c.name),
                required: c.required ?? c.nullable === false,
                isPrimaryKey: c.name === config.primaryKey,
            })),
        [config]
    );

    const editable = useMemo(
        () => columns.filter((c) => !c.isPrimaryKey),
        [columns]
    );

    const [rows, setRows] = useState([]);
    const [listMeta, setListMeta] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [page, setPage] = useState(1);
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formState, setFormState] = useState({});
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    // Describes the whole table (or the whole result set when searching),
    // not the page in front of you.
    const total = listMeta?.total ?? rows.length;
    const recordCount = listMeta?.query
        ? `${total} matching`
        : `${total} records`;

    // Bulk import state
    const [importing, setImporting] = useState(false);
    const [upsertMode, setUpsertMode] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const fileInputRef = useRef(null);

    // Reset form when config changes
    useEffect(() => {
        setFormState(buildInitialState(editable));
        setEditingId(null);
        setFormOpen(false);
        setQ("");
        setDebouncedQ("");
        setPage(1);
    }, [route, editable]);

    // Search hits the server now, so don't fire a query per keystroke.
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedQ(q.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [q]);

    async function loadRows() {
        if (!config) return;
        setLoading(true);
        try {
            const data = await adminApi.list(config.routePath, {
                page,
                size: PAGE_SIZE,
                q: debouncedQ,
            });
            // The backend ships on its own workflow, so this bundle can be
            // newer or older than the API. Tolerate the pre-envelope shape
            // (a bare array) instead of blanking every table on a skew.
            const envelope = Array.isArray(data) ? null : data || {};
            setRows(
                Array.isArray(data)
                    ? data
                    : Array.isArray(envelope.rows)
                      ? envelope.rows
                      : []
            );
            setListMeta(envelope);
            setLoadError(null);
        } catch (e) {
            // Leave rows as-is and record the failure: an errored fetch must
            // not render as "no records yet".
            setLoadError(formatApiError(e));
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    // Depends on `config`, not just `route`: on first paint the manifest is
    // still in flight and there is nothing to fetch yet, so this has to run
    // again once the registry lands.
    useEffect(() => {
        if (config) loadRows();
    }, [route, config, page, debouncedQ]);

    if (manifestLoading) {
        return <div className="p-10 text-navy-500">Loading table registry…</div>;
    }

    if (manifestError) {
        return (
            <div className="p-10 space-y-2">
                <div className="text-red-600 font-medium">
                    Couldn't load the table registry.
                </div>
                <div className="text-xs text-navy-500">
                    {formatApiError(manifestError)}
                </div>
                <Link to="/admin/data" className="text-gold-600 hover:underline">
                    ← Back to data tables
                </Link>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="p-10">
                <div className="text-navy-500">Unknown entity: {route}</div>
                <Link to="/admin/data" className="text-gold-600 hover:underline">
                    ← Back to data tables
                </Link>
            </div>
        );
    }

    // No client-side filtering: `rows` is one server-selected page.
    const filtered = rows;

    function openCreate() {
        setEditingId(null);
        setFormState(buildInitialState(editable));
        setFormOpen(true);
    }

    function openEdit(row) {
        setEditingId(row[config.primaryKey]);
        const next = buildInitialState(editable);
        for (const col of editable) {
            next[col.name] =
                row[col.name] ?? (col.inputType === "checkbox" ? false : "");
        }
        setFormState(next);
        setFormOpen(true);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = normalizeForSubmit(editable, formState);
            if (editingId) {
                await adminApi.update(config.routePath, editingId, payload);
                toast.success(`${config.title} updated`);
            } else {
                await adminApi.create(config.routePath, payload);
                toast.success(`${config.title} created`);
            }
            setFormOpen(false);
            await loadRows();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function confirmDelete() {
        if (!pendingDelete) return;
        try {
            await adminApi.remove(config.routePath, pendingDelete[config.primaryKey]);
            toast.success(`${config.title} deleted`);
            setPendingDelete(null);
            await loadRows();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function downloadTemplate() {
        try {
            const resp = await api.get(
                `/admin/scrud/${config.routePath}/template`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${config.routePath}-template.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function handleImport(file) {
        setImporting(true);
        setImportReport(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/admin/scrud/${config.routePath}/import?upsert=${upsertMode}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setImportReport(data);
            const msg = [];
            if (data.created) msg.push(`${data.created} created`);
            if (data.updated) msg.push(`${data.updated} updated`);
            if (data.skipped) msg.push(`${data.skipped} skipped`);
            toast.success(msg.length ? msg.join(" · ") : "Import complete");
            await loadRows();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    // The registry names the columns worth seeing at a glance; the rest are
    // in the row dialog. Alphabetical order would surface whatever sorts
    // first, which on `users` is three admin_discount_override_* fields.
    const listCols = config.summary
        .map((name) => columns.find((c) => c.name === name))
        .filter(Boolean);
    const readOnly = Boolean(config.readOnly);

    return (
        <>
            <PageHeader
                eyebrow={
                    readOnly ? "Admin · SCRUD · read-only" : "Admin · Data table"
                }
                title={config.title}
                description={
                    readOnly
                        ? `${config.title} is a sensitive table (${recordCount}) — view only. Edits happen through their dedicated flow.`
                        : `Manage the ${config.title} table (${recordCount}).`
                }
                action={
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate("/admin/data")}
                            className="rounded-full h-11 border-navy-700 text-navy-700 hover:bg-navy-50"
                        >
                            <ArrowLeft size={16} className="mr-2" /> All tables
                        </Button>
                        {!readOnly && (
                            <>
                                <Button
                                    variant="outline"
                                    data-testid="scrud-template"
                                    onClick={downloadTemplate}
                                    className="rounded-full h-11 border-navy-700 text-navy-700 hover:bg-navy-50"
                                >
                                    <Download size={16} className="mr-2" /> Template
                                </Button>
                                <Button
                                    variant="outline"
                                    data-testid="scrud-import"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={importing}
                                    className="rounded-full h-11 border-gold-500 text-navy-700 hover:bg-gold-50"
                                >
                                    <Upload size={16} className="mr-2" />
                                    {importing ? "Importing…" : "Import CSV/Excel"}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.csv"
                                    data-testid="scrud-import-input"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) handleImport(f);
                                    }}
                                    className="hidden"
                                />
                                <Button
                                    data-testid="scrud-new"
                                    onClick={openCreate}
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                                >
                                    <Plus size={16} className="mr-2" /> New {config.title}
                                </Button>
                            </>
                        )}
                    </div>
                }
                testid="scrud-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div className="relative max-w-md flex-1">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                        />
                        <Input
                            data-testid="scrud-search"
                            placeholder={`Search ${config.title}…`}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-9 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                        />
                    </div>
                    {!readOnly && (
                        <label
                            className="inline-flex items-center gap-2 text-xs text-navy-500 cursor-pointer select-none"
                            data-testid="scrud-upsert-toggle"
                        >
                            <input
                                type="checkbox"
                                checked={upsertMode}
                                onChange={(e) => setUpsertMode(e.target.checked)}
                                className="accent-navy-700"
                            />
                            Upsert on import
                            <span className="text-navy-400">
                                (overwrite rows with duplicate {config.primaryKey})
                            </span>
                        </label>
                    )}
                </div>

                {importReport && (
                    <ImportReportCard
                        report={importReport}
                        onDismiss={() => setImportReport(null)}
                    />
                )}

                <div className="rounded-2xl bg-white shadow-soft overflow-hidden">
                    <Table data-testid="scrud-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                {listCols.map((c) => (
                                    <TableHead
                                        key={c.name}
                                        className="text-navy-700 uppercase text-xs tracking-[0.2em]"
                                    >
                                        {c.label}
                                    </TableHead>
                                ))}
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em] text-right">
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={listCols.length + 1}
                                        className="text-center text-navy-500 py-10"
                                    >
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={listCols.length + 1}
                                        className="text-center text-navy-500 py-16"
                                    >
                                        <EmptyState
                                            config={config}
                                            readOnly={readOnly}
                                            meta={listMeta}
                                            error={loadError}
                                            query={listMeta?.query}
                                            onRetry={loadRows}
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((row) => (
                                    <TableRow
                                        key={row[config.primaryKey]}
                                        data-testid={`scrud-row-${row[config.primaryKey]}`}
                                    >
                                        {listCols.map((c) => (
                                            <TableCell
                                                key={c.name}
                                                className={
                                                    c.isPrimaryKey
                                                        ? "text-navy-500 font-mono text-xs"
                                                        : "text-navy-700"
                                                }
                                            >
                                                {c.name === "status" && row[c.name] ? (
                                                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                                                        {String(row[c.name])}
                                                    </Badge>
                                                ) : (
                                                    renderCell(
                                                        row[c.name],
                                                        c.inputType || inferType(row[c.name])
                                                    )
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-right">
                                            <div className="inline-flex gap-2">
                                                <Button
                                                    data-testid={`scrud-edit-${row[config.primaryKey]}`}
                                                    onClick={() => openEdit(row)}
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 rounded-full border-navy-100 text-navy-700 hover:bg-navy-50"
                                                >
                                                    <Pencil size={13} className="mr-1" />
                                                    {readOnly ? "View" : "Edit"}
                                                </Button>
                                                {!readOnly && (
                                                    <Button
                                                        data-testid={`scrud-delete-${row[config.primaryKey]}`}
                                                        onClick={() => setPendingDelete(row)}
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                                                    >
                                                        <Trash2 size={13} className="mr-1" /> Delete
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {listMeta?.pages > 1 && (
                    <div
                        data-testid="scrud-pager"
                        className="flex items-center justify-between gap-4 mt-4 text-xs text-navy-500"
                    >
                        <div>
                            Showing{" "}
                            <strong className="text-navy-700">
                                {(listMeta.page - 1) * listMeta.size + 1}–
                                {(listMeta.page - 1) * listMeta.size + rows.length}
                            </strong>{" "}
                            of {listMeta.total}
                            {listMeta.sortedBy && (
                                <span className="ml-2 text-navy-500/70">
                                    · newest first by{" "}
                                    <code className="font-mono">{listMeta.sortedBy}</code>
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                data-testid="scrud-prev-page"
                                disabled={loading || listMeta.page <= 1}
                                onClick={() => setPage((n) => Math.max(1, n - 1))}
                                className="h-8 rounded-full border-navy-100 text-navy-700"
                            >
                                Previous
                            </Button>
                            <span>
                                Page {listMeta.page} of {listMeta.pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                data-testid="scrud-next-page"
                                disabled={loading || listMeta.page >= listMeta.pages}
                                onClick={() => setPage((n) => n + 1)}
                                className="h-8 rounded-full border-navy-100 text-navy-700"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* CREATE / UPDATE dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent
                    data-testid="scrud-dialog"
                    className="bg-white border-navy-100 max-w-3xl max-h-[85vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            {readOnly ? "View" : editingId ? "Edit" : "New"} {config.title}
                        </DialogTitle>
                        <DialogDescription className="text-navy-500">
                            {readOnly
                                ? "This is a sensitive table — record is view-only."
                                : editingId
                                  ? "Update this record. The primary key is immutable."
                                  : `Create a new ${config.title} record. The primary key is generated automatically if left blank.`}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                        <div className="grid md:grid-cols-2 gap-5">
                            {editable.map((col) => {
                                const rendered =
                                    col.inputType || inferType(formState[col.name]);
                                const isFullWidth =
                                    rendered === "wysiwyg" ||
                                    rendered === "textarea" ||
                                    rendered === "json";
                                return (
                                    <div
                                        key={col.name}
                                        className={isFullWidth ? "md:col-span-2" : ""}
                                    >
                                        <FieldRenderer
                                            field={{
                                                ...col,
                                                inputType:
                                                    col.inputType ||
                                                    inferType(formState[col.name]),
                                                nullable: !col.required,
                                            }}
                                            value={formState[col.name]}
                                            onChange={(name, v) =>
                                                setFormState((s) => ({ ...s, [name]: v }))
                                            }
                                            disabled={saving || readOnly}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setFormOpen(false)}
                                className="text-navy-500"
                            >
                                <X size={16} className="mr-1" /> Close
                            </Button>
                            {!readOnly && (
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="scrud-submit"
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white px-6"
                                >
                                    {saving
                                        ? "Saving…"
                                        : editingId
                                          ? "Save changes"
                                          : `Create ${config.title}`}
                                </Button>
                            )}
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* DELETE confirmation */}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && setPendingDelete(null)}
            >
                <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-navy-700">
                            Delete this {config.title}?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-navy-500">
                            This action cannot be undone. The record will be permanently
                            removed from the {config.title} table.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="scrud-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="scrud-delete-confirm"
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Yes, delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function ImportReportCard({ report, onDismiss }) {
    const stats = [
        {
            label: "Created",
            value: report.created,
            tone: "bg-emerald-50 text-emerald-900",
            eyebrow: "text-emerald-800",
        },
        {
            label: "Updated",
            value: report.updated,
            tone: "bg-navy-50 text-navy-900",
            eyebrow: "text-navy-700",
        },
        {
            label: "Skipped",
            value: report.skipped,
            tone: "bg-red-50 text-red-900",
            eyebrow: "text-red-700",
        },
    ];
    return (
        <div
            data-testid="scrud-import-report"
            className="rounded-2xl bg-white border border-gold-200/60 shadow-soft p-5"
        >
            <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="text-gold-600" size={18} />
                <div className="font-display text-lg text-navy-700">
                    {report.filename}
                </div>
                <Badge className="bg-navy-50 text-navy-700 border-navy-100 border ml-1">
                    {report.total_rows} rows parsed
                </Badge>
                <button
                    type="button"
                    onClick={onDismiss}
                    data-testid="scrud-import-report-dismiss"
                    className="ml-auto text-navy-400 hover:text-navy-700"
                    title="Dismiss"
                >
                    <X size={16} />
                </button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
                {stats.map((s) => (
                    <div key={s.label} className={`rounded-lg p-3 ${s.tone}`}>
                        <div
                            className={`text-[10px] uppercase tracking-widest ${s.eyebrow}`}
                        >
                            {s.label}
                        </div>
                        <div className="font-display text-2xl">{s.value}</div>
                    </div>
                ))}
            </div>
            {(report.unknown_headers?.length > 0 || report.errors?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-3">
                    {report.unknown_headers?.length > 0 && (
                        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                            <div className="flex items-center gap-1.5 text-xs text-amber-800 mb-2 font-semibold">
                                <AlertTriangle size={12} /> Ignored headers
                            </div>
                            <div className="text-xs text-amber-900">
                                {report.unknown_headers.join(", ")}
                            </div>
                        </div>
                    )}
                    {report.errors?.length > 0 && (
                        <div className="rounded-lg bg-red-50/60 border border-red-100 p-3">
                            <div className="flex items-center gap-1.5 text-xs text-red-700 mb-2 font-semibold">
                                <AlertTriangle size={12} /> Skipped rows
                            </div>
                            <ul className="text-xs text-red-800 space-y-1 max-h-32 overflow-y-auto">
                                {report.errors.map((e, i) => (
                                    <li key={`${e.row}-${i}`}>
                                        Row {e.row}: {e.reason}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/**
 * What an empty grid actually means. A failed request, a table whose
 * collection was never created, and a table that simply has no rows yet all
 * used to render the identical "No records yet." — which made a broken
 * backend indistinguishable from an unseeded one.
 */
function EmptyState({ config, readOnly, meta, error, query, onRetry }) {
    if (error) {
        return (
            <div className="space-y-2">
                <div className="text-red-600 font-medium">
                    Couldn't load {config.title}.
                </div>
                <div className="text-xs text-navy-500">{error}</div>
                <Button size="sm" variant="outline" onClick={onRetry}>
                    Retry
                </Button>
            </div>
        );
    }

    if (query) {
        return (
            <>
                No records match <strong>{query}</strong>.
            </>
        );
    }

    if (meta?.state === "missing") {
        return (
            <div className="space-y-1">
                <div className="font-medium text-navy-700">No data on this server yet.</div>
                <div className="text-xs text-navy-500">
                    The <code className="font-mono">{meta.collection}</code> collection
                    doesn't exist here.{" "}
                    {readOnly
                        ? "It is created by the app the first time a record is written."
                        : "Importing a CSV will create it."}
                </div>
            </div>
        );
    }

    if (meta?.source) {
        return (
            <div className="space-y-1">
                <div className="font-medium text-navy-700">No records yet.</div>
                <div className="text-xs text-navy-500">
                    This is a live view of{" "}
                    <code className="font-mono">{meta.collection}</code>, which is
                    currently empty.
                </div>
            </div>
        );
    }

    if (readOnly) {
        return <>No records yet.</>;
    }

    return (
        <>
            No records yet. Click <strong>New {config.title}</strong> to add one.
        </>
    );
}
